import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { notification } from 'antd';
import { useServiceError } from '@/hooks';
import {
  listWorkOrderCommissions,
  deleteWorkOrderCommission,
  createWorkOrderCommission,
  listWorkOrderCommissionsByEmployeer,
  addManyWorkOrderCommissions,
} from './WorkOrdersCommissions.service';
import { PageRequest } from '@/core/request/PageRequest';
import { ListWorkOrderCommissionByEmployeerRequest } from './WorkOrdersCommissions.types';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '../WorkOrdersItems/WorkOrdersItems.hooks';

export const WORKORDER_COMMISSIONS_EMPLOYEER_QUERY_KEY =
  'workOrders/commisisons/employeer';

export function useWorkOrderCommissions(params?: {
  complementId?: number;
  itemId?: number;
}) {
  const query = useInfiniteQuery(
    ['workOrders/commisisons', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
      };

      return listWorkOrderCommissions(
        params?.complementId,
        params?.itemId,
        req,
      );
    },
    {
      enabled: !!params?.complementId && !!params?.itemId,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateWorkOrderCommission() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(createWorkOrderCommission, {
    onSuccess(_, vars) {
      notification.success({
        message: 'Comissião cadastrada',
        description: 'A comissão do funcionário foi cadastrada com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          ['workOrders/commisisons'],
          [`${WORKORDER_ITEMS_QUERY_KEY}/verify`],
        ],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          ['workOrders/tracking/calendar/complement', vars.complementId],
          ['workOrders/complements', vars.complementId],
        ],
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteWorkOrderCommission() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (params: { complementId: number; itemId: number; employeeId: number }) =>
      deleteWorkOrderCommission(
        params.complementId,
        params.itemId,
        params.employeeId,
      ),
    {
      onSuccess() {
        queryClient.invalidateQueries(['workOrders/commisisons']);
        queryClient.invalidateQueries([WORKORDER_ITEMS_QUERY_KEY]);
        queryClient.invalidateQueries([
          WORKORDER_COMMISSIONS_EMPLOYEER_QUERY_KEY,
        ]);

        notification.success({
          message: 'Comissião removida',
          description: 'A comissão do funcionário foi removida com sucesso',
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useWorkOrderCommissionsByEmployeer(
  params?: ListWorkOrderCommissionByEmployeerRequest,
) {
  const query = useInfiniteQuery(
    [WORKORDER_COMMISSIONS_EMPLOYEER_QUERY_KEY, { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
      };

      return listWorkOrderCommissionsByEmployeer({ ...req, ...params });
    },
    {
      enabled: !!params?.complementId && !!params?.employeeId,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateManyWorkOrderCommission() {
  const queryClient = useQueryClient();

  return useMutation(addManyWorkOrderCommissions, {
    onSuccess() {
      queryClient.invalidateQueries([
        WORKORDER_COMMISSIONS_EMPLOYEER_QUERY_KEY,
      ]);

      notification.success({
        message: 'Comissões cadastradas',
        description:
          'As comissões do funcionário foram cadastradas com sucesso',
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('workorder.errors.commission_quantity_item_total')
              ? 'Item ultrapassa o quantidade total permitida.'
              : err,
          ),
      });
    },
  });
}
